import React, { useEffect, useState } from 'react';
import {
  Text, View, ActivityIndicator,
  Easing, StyleSheet,
  AppState, Animated as RNAnimated,
  Image,
  Pressable,
  useWindowDimensions,
} from 'react-native';
import Moment from 'moment';
import Animated, {
  useAnimatedStyle, useSharedValue, withRepeat, withTiming,
} from 'react-native-reanimated';
import { RootTabScreenProps } from '../types';
import dashboardAuth from '../requests/Dashboard/DashboardAuth';
import WarningIcon from '../components/60pxIcons/WarningIcon';
import HappyScoreIndicator from '../components/HappyScoreIndicator';
import Colors from '../constants/Colors';
import TargetBar from '../components/Home/TargetBar';
import IntEnvWidget from '../components/Dashboard/IntEnvWidget';
import { HappyScoreCategory, HappyScoreCategoryTitles } from '../constants/Types';
import BoltIconDesktop from '../components/24pxIcons/BoltIconDesktop';
import ChairIconDesktop from '../components/24pxIcons/ChairIconDesktop';
import OccupancyIcon from '../components/24pxIcons/OccupancyIcon';
import CheckCircle from '../components/CheckCircle';
import MonitorIcon from '../components/MonitorIcon';
import SmileIcon from '../components/SmileIcon';
import SunIcon from '../components/SunIcon';
import ScrollViewStyled from '../components/ScrollViewStyled';
import CoffeeIcon from '../components/CoffeeIcon';
import UpdateIcon from '../components/60pxIcons/UpdateIcon';

const clientLogoPlaceholder = require('../assets/images/lazard.png');
const hereworksHappy = require('../assets/images/hereworks-happy.png');
const mcKeon = require('../assets/images/McKeon.png');
const mastercard = require('../assets/images/mastercard-logo.png');
const poweredBy = require('../assets/poweredby.svg');

export default function DashboardScreen({ route }: RootTabScreenProps<'Dashboard'>) {
  type Params = {
    token?: string;
    dev?: boolean;
    client?: string;
    floorExternalId?: string;
    floorName?: string;
  };
  const params = (route?.params ?? {}) as Params;
  const token = params.token ?? 'no token';
  const [authResponse, setAuthResponse] = useState(null);
  const [scoreAnimatedValue] = useState(new RNAnimated.Value(78));
  const [clientName, setClientName] = useState('');
  const [updating, setUpdating] = useState(false);
  const [counter, setCounter] = useState<number>(0);
  const [lastUpdated, setLastUpdated] = useState(Moment());
  const [darkImage, setDarkImage] = useState(false);
  const [floorExternalId, setFloorExternalId] = useState<string | null>(null);
  const [floorName, setFloorName] = useState<string | null>(null);
  const rotation = useSharedValue<number>(0);
  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ rotateZ: `${rotation.value}deg` }],
    opacity: rotation.value > 0 ? 1 : 0,
  }));
  const { width, height } = useWindowDimensions();
  const isQuarterResolution = width === 960 && height === 540;
  const isNarrowScreen = width < 961 && !isQuarterResolution;
  const isPortraitMode = width < height;
  const isUltraWideScreen = width > height * 3;
  const isSplitScreen1080p = width === 960 && height === 1080;
  const getTitleFontSize = () => {
    if (isSplitScreen1080p) return 34;
    if (height <= 540) return 25;
    if (height <= 720) return 34;
    if (height <= 1080) return 48;
    if (height <= 1440) return 64;
    return 96;
  };
  const getDateTextFontSize = () => {
    if (isSplitScreen1080p) return 12;
    if (height <= 540) return 10;
    if (height <= 720 || width <= 440) return 12;
    if (height <= 1080) return 18;
    if (height <= 1440) return 24;
    return 36;
  };
  const getMetricTitleFontSize = () => {
    if (isSplitScreen1080p) return 20;
    if (isQuarterResolution) return 12;
    if (height <= 540) return 12;
    if (height <= 720) return 14;
    if (height <= 1080) return 22;
    if (height <= 1440) return 28;
    return 44;
  };
  const getWidgetTitleFontSize = () => {
    if (isSplitScreen1080p) return 21;
    if (height <= 540) return 16;
    if (height <= 720 || width <= 440) return 21;
    if (height <= 1080) return 32;
    if (height <= 1440) return 42;
    return 64;
  };
  const getWidgetTitleMarginBottom = () => {
    if (isSplitScreen1080p) return 6;
    if (height <= 540) return 6;
    if (height <= 720) return 8;
    if (height <= 1080) return 14;
    if (height <= 1440) return 32;
    return 48;
  };
  const getWidgetPadding = () => {
    if (isSplitScreen1080p) return 14;
    if (height <= 540) return 12;
    if (height <= 720) return 14;
    if (height <= 1080) return 24;
    if (height <= 1440) return 24;
    return 40;
  };
  const getVerticalPadding = () => {
    if (isPortraitMode) return 16;
    if (isSplitScreen1080p) return 24;
    if (height <= 540) return 22;
    if (height <= 720) return 30;
    if (height <= 1080) return 56;
    if (height <= 1440) return 76;
    return 112;
  };
  const getHorizontalPadding = () => {
    if (width <= 440) return 16;
    return 40;
  };
  const getWidgetMarginVertical = () => {
    if (isSplitScreen1080p) return 14;
    if (height <= 540) return 8;
    if (height <= 720) return 12;
    if (height <= 1080) return 16;
    if (height <= 1440) return 20;
    return 24;
  };
  const getScrollViewMarginTop = () => {
    if (isSplitScreen1080p) return 50;
    if (height <= 540) return 10;
    if (height <= 720) return 20;
    if (height <= 1080) return 120;
    if (height <= 1440) return 120;
    return 200;
  };
  const getContainerScale = () => {
    if (isSplitScreen1080p) return 0.7;
    if (height <= 540) return 0.5;
    if (height <= 720) return 0.7;
    if (height <= 1080) return 1;
    if (height <= 1440) return 1.3;
    return 1.6;
  };
  const getSidebarPaddingTop = () => {
    if (isSplitScreen1080p) return 35;
    if (height <= 540) return 20;
    if (height <= 720) return 30;
    if (height <= 1080) return 40;
    if (height <= 1440) return 50;
    return 60;
  };
  const getSidebarPadding = () => {
    if (isSplitScreen1080p) return 22;
    if (isQuarterResolution) return 10;
    if (height <= 540) return 16;
    if (height <= 720) return 20;
    if (height <= 1080) return 24;
    if (height <= 1440) return 32;
    return 40;
  };
  const getSidebarPaddingLeft = () => {
    if (isSplitScreen1080p) return 40;
    if (isQuarterResolution) return 16;
    if (height <= 540) return 24;
    if (height <= 720) return 32;
    if (height <= 1080) return 40;
    if (height <= 1440) return 48;
    return 56;
  };
  const getHappyScoreMarginBottom = () => {
    if (isSplitScreen1080p) return 10;
    if (height <= 540) return 6;
    if (height <= 720) return 12;
    if (height <= 1080) return 16;
    if (height <= 1440) return 20;
    return 24;
  };
  const getHappyScoresMarginHorizontal = () => {
    if (isSplitScreen1080p) return 7;
    if (height <= 540) return 4;
    if (height <= 720) return 6;
    if (height <= 1080) return 8;
    if (height <= 1440) return 10;
    return 12;
  };
  const getWidgetMarginHorizontal = () => {
    if (isSplitScreen1080p) return 10;
    if (height <= 540) return 6;
    if (height <= 720) return 8;
    if (height <= 1080) return 12;
    if (height <= 1440) return 16;
    return 20;
  };
  const getHappyScoreBorderRadius = () => {
    if (isSplitScreen1080p) return 14;
    if (isQuarterResolution) return 6;
    if (height <= 540) return 8;
    if (height <= 720) return 12;
    if (height <= 1080) return 16;
    if (height <= 1440) return 20;
    return 24;
  };
  const getScoreTextMarginLeft = () => {
    if (isSplitScreen1080p) return 10;
    if (height <= 540) return 6;
    if (height <= 720) return 8;
    if (height <= 1080) return 12;
    if (height <= 1440) return 16;
    return 24;
  };
  const getBorderRadius = () => {
    if (isSplitScreen1080p) return 20;
    if (height <= 540) return 12;
    if (height <= 720) return 16;
    if (height <= 1080) return 24;
    if (height <= 1440) return 28;
    return 32;
  };
  const getHappyScoreScale = () => {
    if (isSplitScreen1080p) return 0.7;
    if (height <= 540) return 0.5;
    if (height <= 720) return 0.8;
    if (height <= 1080) return 1.1;
    if (height <= 1440) return 1.2;
    return 1.6;
  };
  const getHappyScoreWidth = () => {
    if (isSplitScreen1080p) return 150;
    if (height <= 540) return 122;
    if (height <= 720) return 180;
    if (height <= 1080) return 250;
    if (height <= 1440) return 290;
    return 350;
  };
  const getHappyScoreHeight = () => {
    if (isSplitScreen1080p) return 130;
    if (height <= 540) return 100;
    if (height <= 720) return 150;
    if (height <= 1080) return 204;
    if (height <= 1440) return 230;
    return 320;
  };
  const getScoreTextFontSize = () => {
    if (isSplitScreen1080p) return 24;
    if (height <= 540) return 18;
    if (height <= 720) return 22;
    if (height <= 1080) return 32;
    if (height <= 1440) return 42;
    return 64;
  };
  const getIconScale = () => {
    if (isSplitScreen1080p) return 0.85;
    if (height <= 540) return 0.5;
    if (height <= 720) return 0.7;
    if (height <= 1080) return 1.3;
    if (height <= 1440) return 1.5;
    return 2;
  };
  const getHappyScorePadding = () => {
    if (isSplitScreen1080p) return 5;
    if (isQuarterResolution) return 6;
    if (height <= 540) return 7;
    if (height <= 720) return 10;
    if (height <= 1080) return 16;
    if (height <= 1440) return 22;
    return 32;
  };
  const getMetricTitleMarginBottom = () => {
    if (isSplitScreen1080p) return 0;
    if (isQuarterResolution) return 5;
    if (height <= 540) return 0;
    if (height <= 720) return 11;
    if (height <= 1080) return 10;
    if (height <= 1440) return 20;
    return 32;
  };
  const getWidgetFlex = (flex: number) => {
    if (isSplitScreen1080p) return undefined;
    if (isPortraitMode) return flex;
    return flex / 2;
  };
  useEffect(() => {
    const isDev = params.dev;
    const routeClientName = params.client;
    setClientName(routeClientName);
    const newFloorExternalId = params.floorExternalId;
    const newFloorName = params.floorName;
    if (newFloorExternalId) {
      setFloorExternalId(newFloorExternalId);
    }
    if (newFloorName) {
      setFloorName(newFloorName);
    }
    dashboardAuth(token, isDev, newFloorExternalId).then((res) => setAuthResponse(res));
  }, [token]);
  useEffect(() => {
    const isDev = params.dev;
    dashboardAuth(token, isDev).then((res) => {
      setAuthResponse(res);
      setUpdating(false);
      rotation.value = 0;
      setLastUpdated(Moment());
    });
  }, [counter]);
  useEffect(() => {
    if (AppState.currentState === 'active') {
      const interval = setInterval(() => {
        setUpdating(true);
        setCounter(counter + 1);
        rotation.value = withRepeat(withTiming(180, {
          duration: 500, easing: Easing.linear,
        }), 0, false);
      }, 60000);
      return () => clearInterval(interval);
    }
    return () => { };
  }, [counter]);
  const getResponse = () => {
    if (authResponse?.isError) return authResponse?.errorText ?? JSON.stringify(authResponse);
    return JSON.stringify(authResponse);
  };
  const getAverageHappyScore = (): number => {
    if (
      !authResponse
      || !authResponse.happyScoreGroupData
      || typeof authResponse.happyScoreGroupData !== 'object'
    ) {
      return 0;
    }
    const dataValues: number[] = Object.values(authResponse.happyScoreGroupData)
      .filter((val): val is number => typeof val === 'number');
    if (dataValues.length === 0) {
      return 0;
    }
    const total = dataValues.reduce((acc, curr) => acc + curr, 0);
    return total / dataValues.length;
  };
  const scoreAnimation: RNAnimated.CompositeAnimation = RNAnimated.parallel([
    RNAnimated.timing(scoreAnimatedValue, {
      duration: 1000,
      useNativeDriver: true,
      easing: (time: number) => Easing.ease(time),
      toValue: getAverageHappyScore(),
    }),
  ]);
  useEffect(() => scoreAnimation.start(), [authResponse]);
  const renderHappyScore = (title, score, iconIndex) => (
    <View style={{
      backgroundColor: '#fff',
      padding: getHappyScorePadding(),
      borderRadius: getHappyScoreBorderRadius(),
      marginBottom: getHappyScoreMarginBottom(),
    }}>
      <Text style={[
        styles.metricTitle,
        {
          fontSize: getMetricTitleFontSize(),
          marginBottom: getMetricTitleMarginBottom(),
          height: getMetricTitleFontSize() * 2.4,
        },
      ]}>
        {HappyScoreCategoryTitles[title]}
      </Text>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <View style={{ transform: [{ scale: getIconScale() }] }}>
          <Icon index={iconIndex} />
        </View>
        <Text style={[styles.scoreText, {
          fontSize: getScoreTextFontSize(),
          marginLeft: getScoreTextMarginLeft(),
        }]}>
          {(authResponse?.happyScoreGroupData && authResponse?.happyScoreGroupData[score])
            ? Math.round(authResponse?.happyScoreGroupData[score])
            : 0}
        </Text>
      </View>
    </View>
  );
  const getImageSource = () => {
    if (clientName && clientName.startsWith('Lazard')) { return clientLogoPlaceholder; }
    if (clientName && clientName.startsWith('Mastercard')) { return mastercard; }
    if (clientName === 'Hereworks') { return hereworksHappy; }
    if (clientName === 'McKeon Group') return mcKeon;
    return clientLogoPlaceholder;
  };
  const Icon = ({ index }) => {
    if (index === 0) return <BoltIconDesktop />;
    if (index === 1) return <SmileIcon />;
    if (index === 2) return <SunIcon />;
    if (index === 3) return <MonitorIcon />;
    if (index === 4) return <CoffeeIcon />;
    if (index === 5) return <OccupancyIcon />;
    if (index === 6) return <CheckCircle />;
    if (index === 7) return <ChairIconDesktop />;
    return null;
  };
  if (!authResponse) {
    return <View style={{
      backgroundColor: '#23283C', flex: 1, justifyContent: 'center', alignItems: 'center',
    }}>
      <Text style={{
        color: '#fff', marginBottom: 20, fontFamily: 'Poppins_500Medium',
      }}>Authenticating...</Text>
      {authResponse ? <Text style={{
        color: '#fff', fontFamily: 'Poppins_500Medium',
      }}>{getResponse()}</Text> : <ActivityIndicator size={'large'} />}
    </View>;
  }
  if (authResponse?.isError) {
    return <View style={{
      backgroundColor: '#23283C', flex: 1, justifyContent: 'center', alignItems: 'center',
    }}>
        <WarningIcon />
        <Text style={{ color: '#fff', fontFamily: 'Poppins_500Medium', marginTop: 20 }}>
          {getResponse()}
        </Text>
      </View>;
  }
  return <View style={{ flexDirection: 'row', flex: 1 }}>
      {floorExternalId ? null : <View style={[styles.sideBar, {
        paddingTop: getSidebarPaddingTop(),
        padding: getSidebarPadding(),
        paddingLeft: getSidebarPaddingLeft(),
      }]}>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
          <View style={{
            width: getHappyScoreWidth(),
            height: getHappyScoreHeight(),
          }}>
            <View style={{ transform: [{ scale: getHappyScoreScale() }], position: 'absolute' }}>
              <HappyScoreIndicator
                happyScoreAnimatedValue={scoreAnimatedValue}
                happyScoreValue={78}
                gradientStartColor="#2BC3A5"
                gradientEndColor="#85E1CF"
                textColor={Colors.light.hereworksExtraDarkTeal} />
            </View>
          </View>
          <View style={{ justifyContent: 'center' }}>
            <Text
              style={[styles.title,
                { fontSize: getTitleFontSize(), lineHeight: getTitleFontSize() }]}>
                  {'Happy\nScore'}
             </Text>
          </View>
        </View>
        <ScrollViewStyled style={{ flex: 1, marginTop: getScrollViewMarginTop() }}>
          {isNarrowScreen ? (
            <View style={[styles.happyScores,
              { marginHorizontal: getHappyScoresMarginHorizontal() }]}>
              {renderHappyScore(HappyScoreCategory.ENERGY, 'group3EnergyAndWaste', 0)}
              {renderHappyScore(HappyScoreCategory.IT, 'group6It', 3)}
              {renderHappyScore(HappyScoreCategory.INTERNAL_ENVIRONMENT, 'group4InternalEnvironment', 4)}
              {renderHappyScore(HappyScoreCategory.AMENITIES, 'group7Amenities', 6)}
              {renderHappyScore(HappyScoreCategory.OCCUPANCY, 'group2BuildingOccupancy', 5)}
              {renderHappyScore(HappyScoreCategory.EXTERNAL_ENVIRONMENT, 'group1ExternalEnvironment', 2)}
              {renderHappyScore(HappyScoreCategory.SATISFACTION, 'group5OccupantSatisfaction', 1)}
              {renderHappyScore(HappyScoreCategory.ERGONOMICS, 'group8ErgonomicsAndNeurodiversity', 7)}
            </View>
          ) : (
            <View style={{ flexDirection: 'row' }}>
              <View style={[styles.happyScores,
                { marginHorizontal: getHappyScoresMarginHorizontal() }]}>
                {renderHappyScore(HappyScoreCategory.ENERGY, 'group3EnergyAndWaste', 0)}
                {renderHappyScore(HappyScoreCategory.IT, 'group6It', 3)}
                {renderHappyScore(HappyScoreCategory.INTERNAL_ENVIRONMENT, 'group4InternalEnvironment', 4)}
                {renderHappyScore(HappyScoreCategory.AMENITIES, 'group7Amenities', 6)}
              </View>
              <View style={[styles.happyScores,
                { marginHorizontal: getHappyScoresMarginHorizontal() }]}>
                {renderHappyScore(HappyScoreCategory.OCCUPANCY, 'group2BuildingOccupancy', 5)}
                {renderHappyScore(HappyScoreCategory.EXTERNAL_ENVIRONMENT, 'group1ExternalEnvironment', 2)}
                {renderHappyScore(HappyScoreCategory.SATISFACTION, 'group5OccupantSatisfaction', 1)}
                {renderHappyScore(HappyScoreCategory.ERGONOMICS, 'group8ErgonomicsAndNeurodiversity', 7)}
              </View>
            </View>
          )}
        </ScrollViewStyled>
        <View style={{
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          transform: [{ scale: getContainerScale() }],
        }}>
          <Pressable
            style={{
              padding: 8, backgroundColor: darkImage ? '#fff' : 'transparent', borderRadius: 12,
            }}
            onPress={() => setDarkImage(!darkImage)}>
            <Image
              source={getImageSource()}
              style={{ height: 72, width: 140, aspectRatio: 1 }}
              resizeMode={'contain'}
            />
          </Pressable>
          <View style={styles.poweredByContainer} /><Image source={poweredBy} /></View>
      </View>}
      <View style={[{
        backgroundColor: '#E6EEF7',
        paddingHorizontal: getHorizontalPadding(),
        paddingVertical: getVerticalPadding(),
        flex: 2,
        alignItems: 'flex-start',
      }, floorExternalId && styles.darkBackground]}>
        {floorExternalId ? <View style={[styles.topBanner, { marginBottom: 12 }]}>
            <Image
              source={getImageSource()}
              style={{
                height: 122, width: 220, aspectRatio: 1,
              }}
              resizeMode={'cover'}
            />
              <Text style={[styles.floorTitle]}>
                {floorName || floorExternalId}
              </Text>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <Text style={[styles.dateText, { fontSize: getDateTextFontSize(), color: '#fff' }]}>
                {lastUpdated.format('MMMM Do HH:mm')}</Text>
              <Pressable style={styles.liveCircle} onPress={() => {
                setUpdating(true);
                setCounter(counter + 1);
                rotation.value = withRepeat(withTiming(180, {
                  duration: 500, easing: Easing.linear,
                }), 0, false);
              }} />
              <Text style={[styles.dateText, { fontSize: getDateTextFontSize(), color: '#fff' }]}>
                {updating ? 'Updating...' : `Updated ${lastUpdated.fromNow()}`}</Text>
            </View>
        </View> : <View>
          {authResponse ? <Text style={[styles.clientTitle, { fontSize: getTitleFontSize() }]}>
            {clientName}
          </Text> : <ActivityIndicator size={'large'} />}
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Text style={[styles.dateText, { fontSize: getDateTextFontSize() }]}>{lastUpdated.format('MMMM Do HH:mm')}</Text>
            <Pressable style={styles.liveCircle} onPress={() => {
              setUpdating(true);
              setCounter(counter + 1);
              rotation.value = withRepeat(withTiming(180, {
                duration: 500, easing: Easing.linear,
              }), 0, false);
            }} />
            <Text style={[styles.dateText, { fontSize: getDateTextFontSize() }]}>{updating ? 'Updating...' : `Updated ${lastUpdated.fromNow()}`}</Text>
          </View>
        </View>}
        <View style={{
          flex: isSplitScreen1080p ? undefined : 5,
          marginVertical: getWidgetMarginVertical(),
          flexDirection: isPortraitMode ? 'column' : 'row',
          alignSelf: 'stretch',
        }}>
          <View style={{
            flex: isPortraitMode ? undefined : getWidgetFlex(3),
            backgroundColor: '#fff',
            marginLeft: (isPortraitMode || floorExternalId) ? 0 : getWidgetMarginHorizontal(),
            borderRadius: getBorderRadius(),
            padding: getWidgetPadding(),
          }}>
            <Text style={[
              styles.widgetTitle,
              {
                fontSize: getWidgetTitleFontSize(),
                marginBottom: getWidgetTitleMarginBottom(),
              },
            ]} numberOfLines={1}>Internal Environment</Text>
            <IntEnvWidget
              data={authResponse?.internalEnvironment}
              loading={false}
            />
          </View>
          {isUltraWideScreen && (
            <View style={{ flex: 1 }}>
              <View style={{
                flex: 1,
                backgroundColor: '#fff',
                marginLeft: getWidgetMarginHorizontal(),
                borderRadius: getBorderRadius(),
                padding: getWidgetPadding(),
                marginBottom: getWidgetMarginHorizontal(),
              }}>
                <Text style={[
                  styles.widgetTitle,
                  {
                    fontSize: getWidgetTitleFontSize(),
                    marginBottom: getWidgetTitleMarginBottom(),
                  },
                ]}>Water</Text>
                <TargetBar current={3100} target={3200} dynamicSize={true} unit={'litres'} />
              </View>
              <View style={{
                flex: 1,
                backgroundColor: '#fff',
                marginLeft: getWidgetMarginHorizontal(),
                borderRadius: getBorderRadius(),
                padding: getWidgetPadding(),
              }}>
                <Text style={[
                  styles.widgetTitle,
                  {
                    fontSize: getWidgetTitleFontSize(),
                    marginBottom: getWidgetTitleMarginBottom(),
                  },
                ]}>Energy</Text>
                <TargetBar current={3400} target={3200} dynamicSize={true} unit={'kW h'} />
              </View>
            </View>
          )}
        </View>
        {(!isUltraWideScreen && !floorExternalId) && (
          <View style={{
            flexDirection: isPortraitMode ? 'column' : 'row',
            alignSelf: 'stretch',
            flex: 2,
          }}>
            <View style={{
              flex: 1,
              backgroundColor: '#fff',
              marginRight: isPortraitMode ? 0 : getWidgetMarginHorizontal(),
              marginBottom: isPortraitMode ? getWidgetMarginHorizontal() : 0,
              borderRadius: getBorderRadius(),
              padding: getWidgetPadding(),
            }}>
              <Text style={[
                styles.widgetTitle,
                {
                  fontSize: getWidgetTitleFontSize(),
                  marginBottom: getWidgetTitleMarginBottom(),
                },
              ]}>Water</Text>
              <TargetBar current={3100} target={3200} dynamicSize={true} unit={'litres'} />
            </View>
            <View style={{
              flex: 1,
              backgroundColor: '#fff',
              marginLeft: isPortraitMode ? 0 : getWidgetMarginHorizontal(),
              borderRadius: getBorderRadius(),
              padding: getWidgetPadding(),
            }}>
              <Text style={[
                styles.widgetTitle,
                {
                  fontSize: getWidgetTitleFontSize(),
                  marginBottom: getWidgetTitleMarginBottom(),
                },
              ]}>Electricity</Text>
              <TargetBar current={3400} target={3200} dynamicSize={true} unit={'kW h'} />
            </View>
          </View>
        )}
      </View>
      <Animated.View style={[animatedStyle, { position: 'absolute', top: 86, right: 40 }]}>
        <UpdateIcon />
      </Animated.View>
    </View>;
}

const styles = StyleSheet.create({
  title: {
    color: '#fff', fontSize: 48, fontFamily: 'Poppins_600SemiBold',
  },
  widgetTitle: {
    color: Colors.light.hereworksBlack600,
    fontSize: 32,
    fontFamily: 'Poppins_400Regular',
    marginBottom: 27,
  },
  happyScores: {
    flex: 1,
  },
  scoreText: {
    lineHeight: 25.6,
    marginLeft: 12,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlue500,
  },
  clientTitle: {
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 48,
    color: Colors.light.hereworksDarkBlue,
    marginBottom: 8,
  },
  floorTitle: {
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 24,
    color: '#fff',
    marginBottom: 8,
  },
  liveCircle: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: Colors.light.hereworksRed500,
    borderWidth: 3,
    borderColor: Colors.light.hereworksLightPink,
    marginHorizontal: 4,
  },
  dateText: {
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksDarkBlue,
  },
  metricTitle: {
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
    height: 48,
  },
  sideBar: {
    backgroundColor: '#23283C', flex: 1,
  },
  topBanner: {
    backgroundColor: '#23283C', alignSelf: 'stretch', marginBottom: 30,
  },
  darkBackground: {
    backgroundColor: '#23283C',
  },
  poweredByContainer: {
    width: 2, height: 98, backgroundColor: '#48537E', marginHorizontal: 27,
  },
});
